import { useCallback, useEffect, useState } from 'react';
import type { PresenceState } from '@makemypartner/shared';
import { supabase } from '@/lib/supabase';

/**
 * A companion's own online switch.
 *
 * MANUAL, and only manual. The companion sets it and unsets it; nothing sets
 * it for them. Closing the tab, losing signal or walking away leaves them
 * showing as online, which is a real limitation and the reason the UI says
 * "you control this" rather than implying it is automatic.
 *
 * Automatic presence — a heartbeat with a Redis TTL, `busy` while a call is
 * live, offline the moment a socket drops — is Phase 3 and needs the socket
 * layer that `lib/socket.ts` currently stubs. Doing the manual half now is
 * worth it because the directory already reads `presence`, so this is the
 * missing write and nothing else.
 *
 * Writes go straight to Supabase rather than through the API: 0018 grants
 * UPDATE on exactly (presence, presence_at, accepts_scheduled) and RLS limits
 * it to the companion's own row, so the database is already the boundary.
 */

export function usePresence() {
  const [presence, setPresence] = useState<PresenceState>('offline');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    void (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) {
        if (active) setLoading(false);
        return;
      }

      const { data, error: err } = await supabase
        .from('companion_profiles')
        .select('presence')
        .eq('id', auth.user.id)
        .maybeSingle();

      if (!active) return;
      if (err) setError(err.message);
      // A companion who has never touched the switch reads back null, which is
      // offline — not an error, and not a reason to show one.
      else setPresence((data?.presence as PresenceState | null) ?? 'offline');
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, []);

  const set = useCallback(async (next: PresenceState) => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return;

    // Optimistic: the switch should move under the finger. Reverted below if
    // the write is refused, so it never lies about what the database holds.
    const previous = presence;
    setPresence(next);
    setError(null);

    const { error: err } = await supabase
      .from('companion_profiles')
      .update({ presence: next, presence_at: new Date().toISOString() })
      .eq('id', auth.user.id);

    if (err) {
      setPresence(previous);
      setError(err.message);
    }
  }, [presence]);

  const toggle = useCallback(
    () => set(presence === 'online' ? 'offline' : 'online'),
    [presence, set],
  );

  return { presence, isOnline: presence === 'online', loading, error, set, toggle };
}
